import { Box, Button, Typography, Link } from '@mui/material'
import { type Model } from 'survey-core'
import { useState } from 'react'
import { Modal } from '../Modal'
import { styles } from './SurveyNavigationButtons.styles'

interface Props {
  survey: Model
  saveSurvey?: (survey: Model, submit?: boolean) => Promise<void>
  onExit?: () => void
}

export function SurveyNavigationButtons ({ survey, saveSurvey, onExit }: Props): JSX.Element {
  const [modal, setModal] = useState<'exit' | 'complete' | null>(null)

  const pageHasErrors = (pageIndex: number): boolean => {
    const page = survey.pages[pageIndex]
    const allQuestions = page.getQuestions(true)
    return allQuestions.some(q => {
      const isActuallyRequired = q.isRequired && q.isVisible
      return isActuallyRequired && q.isEmpty()
    })
  }

  const hasAnyErrors = (): boolean => {
    return survey.pages.some(page => {
      return pageHasErrors(survey.pages.indexOf(page))
    })
  }

  const handleNext = async (): Promise<void> => {
    await saveSurvey?.(survey, false)
    survey.nextPage()
  }

  return (
    <>
      <Box sx={styles.container}>
        {!survey.isFirstPage && (
          <Button
            variant="outlined"
            onClick={() => { survey.prevPage() }}
          >
            Previous
          </Button>
        )}
        {survey.isLastPage
          ? (
            <Button
              variant="contained"
              onClick={async () => {
                if (hasAnyErrors()) {
                  setModal('exit')
                } else {
                  setModal('complete')
                }
              }}
            >
              {hasAnyErrors() ? 'Exit' : 'Submit'}
            </Button>
            )
          : (
            <Button
              variant="contained"
              onClick={() => { void handleNext() }}
            >
              Continue
            </Button>
            )
        }
      </Box>
      {modal === 'exit' && (
        <Modal
          open={true}
          title="Required Fields Incomplete"
          onClose={() => {
            survey.tryComplete()
            setModal(null)
          }}
          onConfirm={async () => {
            setModal(null)
            await saveSurvey?.(survey, false)
            onExit?.()
          }}
          confirmButtonText="Save and Exit"
          cancelButtonText="Continue Editing"
          bodyContent={(
            <>
              <Typography variant="body1">
                Some required fields are still incomplete.
              </Typography>
              <Typography variant="body1" mt={2}>
                Your progress has been saved as a draft. You can return at any time to finish and submit your report.
              </Typography>
              {survey.pages.some((_page, index) => pageHasErrors(index)) && <>
                <Typography variant="body1" mt={2}>
                  Missing fields on:
                </Typography>
                {survey.pages.map((page, index) => {
                  if (pageHasErrors(index)) {
                    return (
                      <Typography key={index} variant="body2" color="error">
                        - <Link
                          component="button"
                          variant="body2"
                          sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={() => {
                            survey.currentPageNo = index
                            setModal(null)
                          }}
                        >
                          {page.title ?? `Page ${index + 1}`}
                        </Link>
                      </Typography>
                    )
                  }
                  return null
                })
                }

              </>
              }
            </>
          )}
        />
      )}
      {modal === 'complete' && (
        <Modal
          open={true}
          title="Submit Mid-Year Progress Report?"
          onClose={() => {
            setModal(null)
          }}
          onConfirm={() => {
            survey.tryComplete()
            setModal(null)
          }}
          confirmButtonText="Continue"
          bodyContent={(
            <>
              <Typography variant="body1">
                All required fields are complete.
              </Typography>
              <Typography variant="body1" mt={2}>
                Submitting your report will finalize it for review.
              </Typography>
            </>
          )}
        />
      )}
    </>
  )
}
